import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { Component, computed, inject, Input, OnDestroy, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { Badge } from 'primeng/badge';
import { Button } from 'primeng/button';
import { Divider } from 'primeng/divider';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { Textarea } from 'primeng/textarea';
import {
  CreateTimeEntryDto,
  TimeEntriesService,
  TimesheetEntryDto,
  WorkItemDto,
  UpdateTimeEntryRequest,
} from '../../../../api';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { MessageService } from '../../../../shared/services/message.service';
import { DateTimeUtils } from '../../../../shared/utils/date-time-utils';
import { finalize, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { TimesheetDayModalData } from '../../model/timesheet-day-modal-data';

@Component({
  selector: 'app-timesheet-day-modal',
  standalone: true,
  imports: [
    ModalComponent,
    Badge,
    Button,
    DatePipe,
    Divider,
    InputNumber,
    InputText,
    NgForOf,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    Select,
    Textarea,
    ToggleSwitch,
  ],
  templateUrl: './timesheet-day-modal.component.html',
  styleUrl: './timesheet-day-modal.component.css',
})
export class TimesheetDayModalComponent implements OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly timeEntriesService = inject(TimeEntriesService);
  private readonly messageService = inject(MessageService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly destroy$ = new Subject<void>();

  private _data?: TimesheetDayModalData;

  readonly dayEntries = signal<TimesheetEntryDto[]>([]);
  readonly loadingEntries = signal(false);
  readonly saving = signal(false);
  readonly selectedEntryId = signal<string | null>(null);
  readonly workItemOptions = computed<WorkItemDto[]>(() => this._data?.workItems ?? []);
  useDurationMode = false;
  private previousTimeValues: Record<'start' | 'end', string> = {
    start: '',
    end: '',
  };

  constructor() {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  readonly selectedEntry = computed(
    () => this.dayEntries().find((entry) => entry.id === this.selectedEntryId()) ?? null,
  );

  readonly orderedDayEntries = computed(() =>
    [...this.dayEntries()].sort((left, right) => {
      const leftStart = left.start ? new Date(left.start).getTime() : 0;
      const rightStart = right.start ? new Date(right.start).getTime() : 0;
      return leftStart - rightStart;
    }),
  );

  readonly previousDayEntries = computed(() =>
    this.orderedDayEntries().filter((entry) => this.isStartedBeforeSelectedDay(entry)),
  );

  readonly currentDayEntries = computed(() =>
    this.orderedDayEntries().filter((entry) => !this.isStartedBeforeSelectedDay(entry)),
  );

  readonly dayTotalDuration = computed(() =>
    this.dayEntries().reduce((acc, entry) => acc + (entry.duration ?? 0), 0),
  );

  readonly dayTotalEarnings = computed(() =>
    this.dayEntries().reduce((acc, entry) => acc + (this.resolveEntryEarnings(entry) ?? 0), 0),
  );

  readonly isReadOnlySelection = computed(
    () => this.selectedEntry()?.editableByCurrentUser === false,
  );

  get modalTitle(): string {
    if (!this._data) {
      return 'Wpisy czasu';
    }

    const day = this.toLocalDate(this._data.dateIso);
    const dayLabel = day.toLocaleDateString('pl-PL', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    return `Wpisy czasu - ${dayLabel}`;
  }

  readonly timeEntryForm = this.fb.group({
    workItemId: this.fb.control<string | null>(null, {
      validators: [Validators.required],
    }),
    dateIso: this.fb.control<string>('', {
      validators: [Validators.required],
    }),
    start: this.fb.control<string>('', {
      validators: [Validators.required, Validators.pattern(/^([01]\d|2[0-3]):[0-5]\d$/)],
    }),
    end: this.fb.control<string>('', {
      validators: [Validators.required, Validators.pattern(/^([01]\d|2[0-3]):[0-5]\d$/)],
    }),
    duration: this.fb.control<number | null>(60, {
      validators: [Validators.required, Validators.min(1)],
    }),
    revenueAmount: this.fb.control<number | null>(null, {
      validators: [Validators.min(0)],
    }),
    description: this.fb.control<string | null>(null, {
      validators: [Validators.maxLength(300)],
    }),
    billable: this.fb.control<boolean>(true, {}),
  });

  @Input()
  set data(value: TimesheetDayModalData | undefined) {
    this._data = value;
    if (!value) {
      return;
    }

    this.resetDraft();
    this.reloadEntries(value.selectedEntryId ?? null);
  }

  get data(): TimesheetDayModalData | undefined {
    return this._data;
  }

  protected selectEntry(entry: TimesheetEntryDto): void {
    if (!entry.id) {
      return;
    }

    this.selectedEntryId.set(entry.id);
    this.patchFormFromEntry(entry);

    if (entry.editableByCurrentUser === false) {
      this.timeEntryForm.disable({ emitEvent: false });
    } else {
      this.timeEntryForm.enable({ emitEvent: false });
    }
  }

  protected startNewEntry(): void {
    this.selectedEntryId.set(null);
    this.resetDraft();
  }

  protected onModeChange(checked: boolean): void {
    this.useDurationMode = checked;
    this.syncModeControls();
    this.recalculateDerivedFields();
  }

  protected onSave(): void {
    if (!this._data || this.timeEntryForm.invalid || this.saving()) {
      return;
    }

    const formValue = this.timeEntryForm.getRawValue();
    const dateIsoValue = formValue.dateIso;
    const startValue = formValue.start;
    const endValue = formValue.end;
    const durationValue = formValue.duration;
    const revenueAmountValue = formValue.revenueAmount;
    const dayData = this._data;

    if (!startValue || !dayData || !dateIsoValue) {
      return;
    }

    const entryDate = this.toLocalDate(dateIsoValue);
    const { duration, endTime } = this.resolveTimeValuesForSave(
      startValue,
      endValue,
      durationValue,
      dateIsoValue,
    );
    if (!duration || !endTime) {
      return;
    }

    const revenueAmount = this.isThresholdBillingMode()
      ? revenueAmountValue != null
        ? Number(revenueAmountValue)
        : undefined
      : undefined;

    this.saving.set(true);

    if (this.selectedEntry()?.id) {
      const selectedEntry = this.selectedEntry()!;
      if (selectedEntry.editableByCurrentUser === false) {
        this.saving.set(false);
        return;
      }

      const payload: UpdateTimeEntryRequest = {
        id: selectedEntry.id,
        workItemId: selectedEntry.workItemId ?? dayData.workItemId ?? '',
        description: formValue.description ?? undefined,
        billable: formValue.billable ?? undefined,
        revenueAmount,
        start: DateTimeUtils.toUTCISO(entryDate, startValue),
        end: endTime,
        duration,
      };

      this.timeEntriesService
        .updateTimeEntryDetails(dayData.workspaceId, dayData.userId, payload)
        .pipe(finalize(() => this.saving.set(false)))
        .subscribe({
          next: () => {
            this.messageService.showSuccess('Wpis czasu został zaktualizowany.');
            this._data?.onChanged?.();
            this.reloadEntries(selectedEntry.id);
          },
          error: (err) => this.errorHandler.handleError(err),
        });
      return;
    }

    const workItemId = formValue.workItemId ?? dayData.workItemId ?? null;
    if (!workItemId) {
      this.saving.set(false);
      return;
    }

    const payload: CreateTimeEntryDto = {
      workItemId,
      duration,
      revenueAmount,
      startTime: DateTimeUtils.toUTCISO(entryDate, startValue),
      endTime,
    };

    this.timeEntriesService
      .createTimeEntry(dayData.workspaceId, dayData.userId, payload)
      .pipe(
        switchMap((createdEntry) => {
          if (!formValue.description && formValue.billable !== false) {
            return of(createdEntry);
          }

          const updatePayload: UpdateTimeEntryRequest = {
            id: createdEntry.id!,
            workItemId,
            description: formValue.description ?? undefined,
            billable: formValue.billable ?? undefined,
            revenueAmount,
            start: DateTimeUtils.toUTCISO(entryDate, startValue),
            end: endTime,
            duration,
          };

          return this.timeEntriesService
            .updateTimeEntryDetails(dayData.workspaceId, dayData.userId, updatePayload)
            .pipe(map(() => createdEntry));
        }),
      )
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.messageService.showSuccess('Dodano wpis czasu.');
          this._data?.onChanged?.();
          this.reloadEntries();
          this.resetDraft();
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  protected onDeleteSelectedEntry(): void {
    if (!this._data || !this.selectedEntry()?.id || this.selectedEntry()?.editableByCurrentUser === false) {
      return;
    }

    const entryId = this.selectedEntry()!.id!;
    this.saving.set(true);

    this.timeEntriesService
      .deleteTimeEntry(this._data.workspaceId, this._data.userId, [entryId])
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.messageService.showInfo('Usunięto wpis.');
          this._data?.onChanged?.();
          this.reloadEntries();
          this.resetDraft();
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  protected formatDuration(minutes?: number | null): string {
    if (!minutes) {
      return '0 min';
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${remainingMinutes} min`;
    }

    if (remainingMinutes === 0) {
      return `${hours} h`;
    }

    return `${hours} h ${remainingMinutes} min`;
  }

  protected formatMoney(value?: number | null): string {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);
  }

  protected trackByEntryId(_: number, entry: TimesheetEntryDto): string | undefined {
    return entry.id;
  }

  protected resolveEntryEarnings(entry: TimesheetEntryDto): number | null {
    return entry.entryEarnings ?? entry.earnings ?? null;
  }

  protected get displayWorkItem(): string {
    if (!this._data) {
      return '';
    }

    const parts = [this._data.workItemName, this._data.clientName].filter(Boolean);
    return parts.join(' - ');
  }

  protected canSaveCurrentSelection(): boolean {
    if (!this._data || this.saving()) {
      return false;
    }

    if (this.selectedEntry()?.id && this.selectedEntry()?.editableByCurrentUser === false) {
      return false;
    }

    return this.isCurrentModeValid();
  }

  protected isSelectedEntry(entry: TimesheetEntryDto): boolean {
    return this.selectedEntryId() === entry.id;
  }

  protected isStartedBeforeSelectedDay(entry: TimesheetEntryDto): boolean {
    if (!this._data?.dateIso || !entry.start) {
      return false;
    }

    return new Date(entry.start).getTime() < this.toLocalDate(this._data.dateIso).getTime();
  }

  private reloadEntries(selectedEntryId: string | null = null): void {
    if (!this._data) {
      return;
    }

    this.loadingEntries.set(true);
    this.timeEntriesService
      .getDayEntries(this._data.workspaceId, this._data.userId, this._data.dateIso)
      .pipe(finalize(() => this.loadingEntries.set(false)))
      .subscribe({
        next: (entries) => {
          this.dayEntries.set(entries);
          if (selectedEntryId) {
            const selected = entries.find((entry) => entry.id === selectedEntryId);
            if (selected) {
              this.selectEntry(selected);
              return;
            }
          }

          this.startNewEntry();
        },
        error: (err) => this.errorHandler.handleError(err),
      });
  }

  private resetDraft(): void {
    this.selectedEntryId.set(null);
    this.timeEntryForm.reset({
      workItemId: this.getDefaultWorkItemId(),
      dateIso: this._data?.dateIso ?? '',
      start: '08:00',
      end: '09:00',
      duration: 60,
      description: null,
      billable: true,
      revenueAmount: null,
    });
    this.timeEntryForm.enable({ emitEvent: false });
    this.syncModeControls();
    this.recalculateDerivedFields();
  }

  private getDefaultWorkItemId(): string | null {
    if (this._data?.workItemId) {
      return this._data.workItemId;
    }

    if ((this._data?.workItems?.length ?? 0) === 1) {
      return this._data?.workItems?.[0]?.id ?? null;
    }

    return null;
  }

  private patchFormFromEntry(entry: TimesheetEntryDto): void {
    const start = entry.start ? this.toLocalTime(entry.start) : '08:00';
    const end = entry.end ? this.toLocalTime(entry.end) : this.addMinutesToTime(start, entry.duration ?? 60);

    this.timeEntryForm.patchValue(
      {
        workItemId: entry.workItemId ?? this._data?.workItemId ?? this.getDefaultWorkItemId(),
        dateIso: entry.start ? this.toDateInputValue(new Date(entry.start)) : this._data?.dateIso ?? '',
        start,
        end,
        duration: entry.duration ?? 60,
        revenueAmount: entry.revenueAmount ?? null,
        description: entry.description ?? null,
        billable: entry.billable ?? true,
      },
      { emitEvent: false },
    );
    this.syncModeControls();
    this.recalculateDerivedFields();
  }

  protected onTimeClick(controlName: 'start' | 'end', $event: Event): void {
    const control = this.timeEntryForm.get(controlName);
    if (!control) return;

    this.previousTimeValues[controlName] = control.value ?? '';

    const input = $event.target as HTMLInputElement;
    input.value = input.value.replace(':', '');
    input.select();
  }

  protected onChangeTimeEntry(controlName: 'start' | 'end'): void {
    const control = this.timeEntryForm.get(controlName);
    if (!control) return;

    let value: string = control.value?.trim() ?? '';
    value = value.replace(':', '');

    if (!/^\d{3,4}$/.test(value)) {
      control.setValue(this.previousTimeValues[controlName], { emitEvent: false });
      this.recalculateDerivedFields();
      return;
    }

    if (value.length === 3) {
      value = '0' + value;
    }

    const hours = Number(value.slice(0, 2));
    const minutes = Number(value.slice(2, 4));

    if (hours > 23 || minutes > 59) {
      control.setValue(this.previousTimeValues[controlName], { emitEvent: false });
      this.recalculateDerivedFields();
      return;
    }

    const normalized = `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;

    control.setValue(normalized, { emitEvent: false });
    this.recalculateDerivedFields();
  }

  protected onDurationInput(): void {
    this.recalculateDerivedFields();
  }

  private toLocalTime(isoString: string): string {
    const date = new Date(isoString);
    return `${date.getHours().toString().padStart(2, '0')}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  }

  private toLocalDate(dateIso: string): Date {
    const [year, month, day] = dateIso.split('-').map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  private toDateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private buildEndIso(entryDate: Date, start: string, duration: number): string {
    const [hours, minutes] = start.split(':').map(Number);
    const endDate = new Date(entryDate);
    endDate.setHours(hours, minutes, 0, 0);
    endDate.setMinutes(endDate.getMinutes() + duration);
    return endDate.toISOString();
  }

  private addMinutesToTime(start: string, duration: number): string {
    const [hours, minutes] = start.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + duration;
    const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
    const resultHours = Math.floor(normalized / 60);
    const resultMinutes = normalized % 60;
    return `${resultHours.toString().padStart(2, '0')}:${resultMinutes.toString().padStart(2, '0')}`;
  }

  private parseTimeToMinutes(value: string | null | undefined): number | null {
    if (!value || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
      return null;
    }

    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private resolveTimeValuesForSave(
    startValue: string,
    endValue: string | null,
    durationValue: number | null,
    dateIsoValue = this._data?.dateIso,
  ): { duration: number | null; endTime: string | null } {
    const dateIso = dateIsoValue;
    if (!dateIso) {
      return { duration: null, endTime: null };
    }

    const entryDate = this.toLocalDate(dateIso);

    if (this.useDurationMode) {
      if (durationValue == null || !Number.isFinite(Number(durationValue)) || Number(durationValue) <= 0) {
        return { duration: null, endTime: null };
      }

      const duration = Number(durationValue);
      return {
        duration,
        endTime: this.buildEndIso(entryDate, startValue, duration),
      };
    }

    const safeEndValue = endValue;
    if (!safeEndValue) {
      return { duration: null, endTime: null };
    }

    const startMinutes = this.parseTimeToMinutes(startValue);
    const endMinutes = this.parseTimeToMinutes(safeEndValue);
    if (startMinutes == null || endMinutes == null) {
      return { duration: null, endTime: null };
    }

    let durationMinutes = endMinutes - startMinutes;
    const endDate = new Date(entryDate);
    if (durationMinutes < 0) {
      durationMinutes += 24 * 60;
      endDate.setDate(endDate.getDate() + 1);
    }

    const [hours, minutes] = safeEndValue.split(':').map(Number);
    endDate.setHours(hours, minutes, 0, 0);

    return {
      duration: durationMinutes,
      endTime: endDate.toISOString(),
    };
  }

  private recalculateDerivedFields(): void {
    const start = this.timeEntryForm.controls.start.value;
    const end = this.timeEntryForm.controls.end.value;
    const duration = this.timeEntryForm.controls.duration.value;

    if (this.useDurationMode) {
      if (start && duration != null && Number.isFinite(Number(duration)) && Number(duration) > 0) {
        this.timeEntryForm.controls.end.setValue(this.addMinutesToTime(start, Number(duration)), {
          emitEvent: false,
        });
      }
      return;
    }

    const startMinutes = this.parseTimeToMinutes(start);
    const endMinutes = this.parseTimeToMinutes(end);

    if (startMinutes == null || endMinutes == null) {
      return;
    }

    let durationMinutes = endMinutes - startMinutes;
    if (durationMinutes < 0) {
      durationMinutes += 24 * 60;
    }

    this.timeEntryForm.controls.duration.setValue(durationMinutes, { emitEvent: false });
  }

  private syncModeControls(): void {
    if (this.useDurationMode) {
      this.timeEntryForm.controls.duration.enable({ emitEvent: false });
      this.timeEntryForm.controls.end.disable({ emitEvent: false });
      return;
    }

    this.timeEntryForm.controls.end.enable({ emitEvent: false });
    this.timeEntryForm.controls.duration.disable({ emitEvent: false });
  }

  private isCurrentModeValid(): boolean {
    if (!this.selectedEntry()) {
      const workItemId = this.timeEntryForm.controls.workItemId.value;
      if (!workItemId) {
        return false;
      }
    }

    const start = this.timeEntryForm.controls.start.value;
    const startControl = this.timeEntryForm.controls.start;

    if (!start || startControl.invalid) {
      return false;
    }

    if (this.useDurationMode) {
      const duration = this.timeEntryForm.controls.duration.value;
      if (!(duration != null && Number.isFinite(Number(duration)) && Number(duration) > 0)) {
        return false;
      }
    } else {
      const end = this.timeEntryForm.controls.end.value;
      if (!end || !this.timeEntryForm.controls.end.valid) {
        return false;
      }
    }

    if (this.isThresholdBillingMode()) {
      const revenueAmount = this.timeEntryForm.controls.revenueAmount.value;
      return revenueAmount != null && Number.isFinite(Number(revenueAmount)) && Number(revenueAmount) >= 0;
    }

    return true;
  }

  protected isThresholdBillingMode(): boolean {
    const workItemId = this.selectedEntry()?.workItemId ?? this.timeEntryForm.controls.workItemId.value ?? this._data?.workItemId ?? this.getDefaultWorkItemId();
    if (!workItemId) {
      return false;
    }

    const workItem = this.workItemOptions().find((item) => item.id === workItemId);
    return workItem?.billingMode === WorkItemDto.BillingModeEnum.RevenueThreshold;
  }
}
